// src/AppRouter.js
import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from "./TermsOfService";
import HomePage from './Homepage';
import Header from './Header';
import PhysiotherapistsPage from './PhysiotherapistsPage';
import ResearchSolutionPage from './ResearchSolutionPage';
import BlogPage from './BlogPage';
import BlogPostPage from './BlogPostPage';
import ProfilePage from './ProfilePage';
import SubscribePage from './SubscribePage';
import StudyPage from './StudyPage';
import AboutPage from './AboutPage';
import HowItWorksPage from './HowItWorksPage';
import CmpxPage from './CmpxPage';
import ForEmployersPage from './ForEmployersPage';
import GetStartedPage from './GetStartedPage';
import IndividualsPage from './IndividualsPage';
import FAQPage from './FAQPage';
import SteadyStridePage from './SteadyStridePage';
import MskCostPage from './MskCostPage';
import TrustCentrePage from './TrustCentrePage';
import WhoWritesPage from './WhoWritesPage';
import ResearchBehindPage from './ResearchBehindPage';
import Footer from './Footer';
import CookieConsent from './CookieConsent';
// import AcademyPage from './AcademyPage'; // Hidden: Academy is still a prototype
import ProgramWizardPage from './ProgramWizardPage';
import LayoutMockups from './LayoutMockups';
import UnderReview from './components/UnderReview';
import { isProductionHost } from './utils/environment';
import { captureTrafficContext } from './utils/trafficContext';

const PRODUCTION = isProductionHost();

// Captures first-touch utm params, landing page and referrer once per
// session, before any navigation has a chance to strip them. See
// utils/trafficContext.js.
function TrafficCapture() {
    useEffect(() => {
        captureTrafficContext();
    }, []);
    return null;
}

const BLOG_HELD = 'The articles are written and in clinical review. We publish health content only once a registered clinician has checked it and put their name to it, so nothing is up yet.';

// Block search engine indexing on non-production environments
function NoIndexGuard() {
    useEffect(() => {
        if (!isProductionHost()) {
            const meta = document.createElement('meta');
            meta.name = 'robots';
            meta.content = 'noindex, nofollow';
            document.head.appendChild(meta);
            return () => document.head.removeChild(meta);
        }
    }, []);
    return null;
}

// Scroll to top component
function ScrollToTop() {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        // A hash means the link is asking for a specific section, so honour it
        // rather than overriding it. Without this every anchored link — the
        // header's Contact us among them — lands at the top of the page instead.
        if (hash) {
            const target = document.querySelector(hash);
            if (target) {
                target.scrollIntoView();
                // The section may not have painted yet on a fresh route.
                setTimeout(() => {
                    const t = document.querySelector(hash);
                    if (t) t.scrollIntoView();
                }, 60);
                return;
            }
        }

        // Force scroll to top using multiple methods for maximum compatibility
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        // Additional backup with slight delay
        setTimeout(() => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        }, 10);
    }, [pathname, hash]);

    return null;
}

export default function AppRouter() {
    return (
        <div className="min-h-screen">
            <NoIndexGuard />
            <ScrollToTop />
            <TrafficCapture />
            {/* Sticky Header - appears on all pages */}
            <Header />

            {/* Page Content */}
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/steadystride" element={<SteadyStridePage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/cmpx" element={<CmpxPage />} />
                {/* The employer surface. /cmpx is the product from four points of
                    view; this is the employer case in employer words, and it is
                    where the landing page's section 2 exit lands. */}
                {/* Audience pages are plain nouns: /employers, /individuals,
                    /physiotherapists, /researchers. The old paths 301 rather than
                    disappear — /getstarted in particular is on a printed QR code
                    and in published social posts. */}
                <Route path="/employers" element={<ForEmployersPage />} />
                <Route path="/for-employers" element={<Navigate to="/employers" replace />} />
                {/* Employer spine, added 2026-08-31. /msk-cost is the single
                    contact destination — the header CTA points here. */}
                {/* /contact is the page, not a redirect to one. Pointing the header
                    at /msk-cost#contact worked but left that compound URL in the
                    address bar and in anything anyone copied out of it. The form sits
                    beside the calculator at the top of the page, so landing plainly on
                    /contact already shows it and no anchor is needed.

                    Trade-off taken deliberately: the slug no longer carries "msk-cost".
                    The H1, the copy and the sources table still do, and a slug is a
                    weak signal next to those. /msk-cost redirects so nothing breaks. */}
                <Route path="/contact" element={<MskCostPage />} />
                <Route path="/msk-cost" element={<Navigate to="/contact" replace />} />
                <Route path="/trust-centre" element={<TrustCentrePage />} />
                {/* Sits under /blog because it is explanatory content, not a product
                    surface. React Router matches the static segment ahead of
                    /blog/:slug, so it takes precedence over the blob-backed posts.
                    The old top-level path redirects — it was only ever on QA, but a
                    redirect costs nothing and the sitemap had it. */}
                <Route path="/blog/who-writes-your-programme" element={<WhoWritesPage />} />
                {/* The three footer credit claims are explained here, one anchor each. */}
                <Route path="/blog/the-research" element={<ResearchBehindPage />} />
                <Route path="/who-writes-your-programme" element={<Navigate to="/blog/who-writes-your-programme" replace />} />
                {/* Consolidated 2026-06-29: dedicated /capture, /insights, /outcomes pages
                    folded into the single /how-it-works combined page (per reference mockup
                    #how). Old routes redirect for SEO continuity + bookmark preservation. */}
                <Route path="/capture" element={<Navigate to="/how-it-works" replace />} />
                <Route path="/insights" element={<Navigate to="/how-it-works" replace />} />
                <Route path="/outcomes" element={<Navigate to="/how-it-works" replace />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/TermsOfService" element={<TermsOfService />} />
                {/* SmartDiary is retired. The URL is in the live production sitemap,
                    so it redirects rather than 404s. Server-side 301 in
                    staticwebapp.config.json is the one that counts; this is the
                    fallback if anything reaches React first. */}
                <Route path="/smartdiary" element={<Navigate to="/individuals" replace />} />
                <Route path="/join-us" element={<Navigate to="/physiotherapists" replace />} />
                {/* /solutions hub killed 2026-06-29 per Jimmy: vestigial after the B2
                    audience-led nav rebalance. Old direct-URL bookmarks redirect to /. */}
                <Route path="/solutions" element={<Navigate to="/" replace />} />
                {/* Audience page for individuals. Pairs with /employers; the two
                    product pages, /steadystride and /cmpx, are reached from the footer.

                    They diverged on 4 Sep. /individuals is the audience page;
                    /getstarted is a router that sends the reader to whichever of the four
                    is theirs. The component that used to serve both is now
                    IndividualsPage.js, and GetStartedPage.js is the new entry page.

                    (An earlier comment here claimed a printed QR code and social posts
                    point at /getstarted. That was never verified and appears to be
                    wrong: nothing in the repo or the Assets library points at it. The
                    only inbound is the error-state button on ProfilePage.) */}
                <Route path="/individuals" element={<IndividualsPage/>}/>
                <Route path="/getstarted" element={<GetStartedPage/>}/>
                {/* /solutions/* collapsed 2026-08-31 (CMO QA review, QA-01 + QA-10).
                    The namespace encoded four persona families, and a site built from
                    four persona families needs four homepages. The employer surface is
                    the homepage now, so these stop being one solution among four and
                    become plainly-named supporting surfaces. Old paths redirect.

                    /solutions/corporate is deleted rather than unlinked: it asserted
                    "reduce avoidable absence", a retired claim, and its own copy said
                    it stayed reachable by direct link for outreach — so unlinked and
                    reachable is the state it was already in. Outreach links land on
                    the employer spine instead. */}
                <Route path="/physiotherapists" element={<PhysiotherapistsPage/>}/>
                <Route path="/researchers" element={<ResearchSolutionPage/>}/>
                <Route path="/solutions/patients" element={<Navigate to="/individuals" replace />}/>
                <Route path="/solutions/physiotherapists" element={<Navigate to="/physiotherapists" replace />}/>
                <Route path="/solutions/research" element={<Navigate to="/researchers" replace />}/>
                <Route path="/solutions/corporate" element={<Navigate to="/" replace />}/>
                {/* <Route path="/academy" element={<AcademyPage/>}/> Hidden: Academy is still a prototype */}
                {/* /data retired 3 Sep 2026. It was a thin page whose whole job was
                    pointing at the privacy notice and the terms, which is now the trust
                    centre's #documents section. Nothing on the site linked to it, so it
                    was an orphan competing with the trust centre for the same query. */}
                <Route path="/data" element={<Navigate to="/trust-centre" replace />} />
                {/* The blog is visible on QA and held on the live domain.
                    The URLs stay live either way: the footer credit lines and
                    several in-page links point into /blog, and a holding page
                    that offers the form beats a 404 or a dead link.

                    Both routes show the same holding page on production. The
                    two named article routes above carry their own titles, but
                    a blob-backed post cannot be titled without fetching it,
                    and inventing a title for a page we are withholding would
                    be worse than naming the section. */}
                <Route path="/blog" element={PRODUCTION ? <UnderReview title="Articles in review" category="Blog" intro={BLOG_HELD} /> : <BlogPage/>}/>
                <Route path="/blog/:slug" element={PRODUCTION ? <UnderReview title="Articles in review" category="Blog" intro={BLOG_HELD} /> : <BlogPostPage/>}/>
                <Route path="/subscribe" element={<SubscribePage/>}/>
                <Route path="/profile" element={<Navigate to="/subscribe" replace />}/>
                <Route path="/profile/:profileSlug" element={<ProfilePage/>}/>
                <Route path="/research" element={<StudyPage/>}/>
                <Route path="/research/:studySlug" element={<StudyPage/>}/>
                <Route path="/onboarding" element={<ProgramWizardPage/>}/>
                {/* /program-wizard renamed to /onboarding 2026-06-29 (plain English for the
                    physio onboarding + service-setup flow). Old route redirects. */}
                <Route path="/program-wizard" element={<Navigate to="/onboarding" replace />} />
                <Route path="/layout-mockups" element={<LayoutMockups/>}/>
                {/* Legacy /advisor routes redirect via staticwebapp.config.json */}
                {/* Add more routes here as you create more pages */}

            </Routes>
            <Footer></Footer>

            {/* Cookie Consent Banner */}
            <CookieConsent />
        </div>
    );
}
